import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, User, Home, CreditCard, AlertCircle, CheckCircle, Clock, Download, Printer } from 'lucide-react';
import api from '../services/api';
import Modal from './Common/Modal';
import PaymentModal from './PaymentModal';
import TransferModal from './TransferModal';
import DepositRefundModal from './DepositRefundModal';

const LeaseDetailsModal = ({ isOpen, onClose, lease, onPaymentComplete }) => {
    const [leaseDetails, setLeaseDetails] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showDepositRefundModal, setShowDepositRefundModal] = useState(false);
    const [paymentSummary, setPaymentSummary] = useState(null);

    useEffect(() => {
        if (isOpen && lease && lease.id) {
            fetchLeaseDetails();
            fetchPayments();
        } else if (isOpen && !lease) {
            console.warn('LeaseDetailsModal: lease prop is null or undefined');
        }
    }, [isOpen, lease]);

    const fetchLeaseDetails = async () => {
        try {
            if (!lease || !lease.id) return;
            const response = await api.get(`/leases/${lease.id}`);
            setLeaseDetails(response.data.data);
        } catch (error) {
            console.error('Error fetching lease details:', error);
        }
    };

    const fetchPayments = async () => {
        try {
            if (!lease || !lease.id) return;
            setLoading(true);
            const response = await api.get(`/leases/${lease.id}/payments`);
            const paymentsData = response.data.data?.data || [];
            setPayments(paymentsData);
            calculatePaymentSummary(paymentsData);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const calculatePaymentSummary = (paymentsData) => {
        if (!lease || !lease.rent_amount) {
            console.warn('Cannot calculate summary: lease or rent_amount missing');
            return;
        }
        
        try {
            const startDate = new Date(lease.start_date);
            const endDate = new Date(lease.end_date);
            const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                          (endDate.getMonth() - startDate.getMonth());
            
            const expectedTotal = parseFloat(lease.rent_amount) * months;
            const paidAmount = (paymentsData || []).filter(p => p.status === 'completed')
                .reduce((sum, p) => sum + parseFloat(p.amount), 0);
            const pendingAmount = (paymentsData || []).filter(p => p.status === 'pending')
                .reduce((sum, p) => sum + parseFloat(p.amount), 0);
            
            // Calculate next due date
            const today = new Date();
            const nextDueDate = new Date(today);
            const dueDay = lease.payment_due_day || 1;
            nextDueDate.setDate(dueDay);
            if (nextDueDate < today) {
                nextDueDate.setMonth(nextDueDate.getMonth() + 1);
            }
            
            // Check if payment is overdue
            const isOverdue = nextDueDate < today && paidAmount < expectedTotal;
            
            // Calculate days until due or overdue
            const daysUntilDue = Math.ceil((nextDueDate - today) / (1000 * 60 * 60 * 24));
            
            setPaymentSummary({
                expected_total: expectedTotal,
                paid_amount: paidAmount,
                pending_amount: pendingAmount,
                balance: expectedTotal - paidAmount,
                next_due_date: nextDueDate,
                is_overdue: isOverdue,
                days_until_due: daysUntilDue,
                payment_progress: expectedTotal > 0 ? (paidAmount / expectedTotal) * 100 : 0,
                months_paid: expectedTotal > 0 ? Math.floor(paidAmount / parseFloat(lease.rent_amount)) : 0,
                months_total: months
            });
        } catch (error) {
            console.error('Error calculating payment summary:', error);
        }
    };

    // Function to handle marking payment as completed
    const markAsCompleted = async (paymentId) => {
        if (window.confirm('Mark this payment as completed?')) {
            try {
                await api.put(`/payments/${paymentId}/status`, { status: 'completed' });
                fetchPayments();
                if (onPaymentComplete) onPaymentComplete();
                alert('Payment marked as completed!');
            } catch (error) {
                console.error('Error updating payment:', error);
                alert('Failed to update payment status');
            }
        }
    };

    // Function to download receipt
    const downloadReceipt = async (paymentId) => {
        try {
            const response = await api.get(`/payments/${paymentId}/receipt`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `receipt_${paymentId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading receipt:', error);
            alert('Failed to download receipt');
        }
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            completed: 'bg-green-100 text-green-800',
            pending: 'bg-yellow-100 text-yellow-800',
            processing: 'bg-blue-100 text-blue-800',
            failed: 'bg-red-100 text-red-800',
            refunded: 'bg-gray-100 text-gray-800'
        };
        return badges[status] || badges.pending;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'pending': return <Clock className="h-4 w-4 text-yellow-600" />;
            case 'failed': return <AlertCircle className="h-4 w-4 text-red-600" />;
            default: return <CreditCard className="h-4 w-4 text-gray-600" />;
        }
    };

    const formatCurrency = (amount) => {
        return `KES ${(amount || 0).toLocaleString()}`;
    };

    const handlePrint = () => {
        window.print();
    };

    // Don't render if no lease
    if (!lease || !lease.id) {
        return null;
    }

    // Show loading state while fetching data
    if (loading && !leaseDetails) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title="Lease Details" size="lg">
                <div className="text-center py-8">Loading lease details...</div>
            </Modal>
        );
    }

    const currentLease = leaseDetails || lease;

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title="Lease Details" size="lg">
                <div className="space-y-6 print:p-4">
                    {/* Header with Print Button */}
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{currentLease.asset_name || 'Property'}</h2>
                            <p className="text-gray-600">Unit {currentLease.unit_number || 'N/A'} • Lease #{currentLease.lease_number || 'N/A'}</p>
                        </div>
                        <div className="flex space-x-2 print:hidden">
                            {parseFloat(currentLease.deposit_amount || 0) > 0 && !currentLease.deposit_refunded && (
                                <button
                                    onClick={() => setShowDepositRefundModal(true)}
                                    className="flex items-center px-3 py-1 border border-green-300 text-green-700 rounded-md hover:bg-green-50"
                                >
                                    <DollarSign className="h-4 w-4 mr-1" />
                                    Refund Deposit
                                </button>
                            )}
                            {currentLease.status === 'active' && (
                                <button
                                    onClick={() => setShowTransferModal(true)}
                                    className="flex items-center px-3 py-1 border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50"
                                >
                                    <Home className="h-4 w-4 mr-1" />
                                    Transfer
                                </button>
                            )}
                            <button
                                onClick={handlePrint}
                                className="flex items-center px-3 py-1 border rounded-md hover:bg-gray-50"
                            >
                                <Printer className="h-4 w-4 mr-1" />
                                Print
                            </button>
                        </div>
                    </div>

                    {/* Payment Summary Cards */}
                    {paymentSummary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className={`rounded-lg p-4 ${paymentSummary.is_overdue ? 'bg-red-50 border border-red-200' : 'bg-green-50'}`}>
                                <p className="text-sm text-gray-600">Monthly Rent</p>
                                <p className="text-xl font-bold text-gray-900">{formatCurrency(currentLease.rent_amount)}</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4">
                                <p className="text-sm text-gray-600">Total Paid</p>
                                <p className="text-xl font-bold text-green-600">{formatCurrency(paymentSummary.paid_amount)}</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-4">
                                <p className="text-sm text-gray-600">Balance</p>
                                <p className="text-xl font-bold text-red-600">{formatCurrency(paymentSummary.balance)}</p>
                            </div>
                            <div className={`rounded-lg p-4 ${paymentSummary.is_overdue ? 'bg-red-100' : 'bg-purple-50'}`}>
                                <p className="text-sm text-gray-600">Next Due Date</p>
                                <p className={`text-lg font-bold ${paymentSummary.is_overdue ? 'text-red-600' : 'text-purple-600'}`}>
                                    {paymentSummary.next_due_date?.toLocaleDateString() || 'N/A'}
                                    {paymentSummary.is_overdue && (
                                        <span className="text-xs ml-1">(Overdue)</span>
                                    )}
                                </p>
                                {!paymentSummary.is_overdue && paymentSummary.days_until_due > 0 && (
                                    <p className="text-xs text-gray-500">Due in {paymentSummary.days_until_due} days</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Payment Progress Bar */}
                    {paymentSummary && (
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Payment Progress</span>
                                <span className="font-medium">
                                    {paymentSummary.months_paid} / {paymentSummary.months_total} months paid
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-indigo-600 rounded-full h-2 transition-all duration-500"
                                    style={{ width: `${Math.min(paymentSummary.payment_progress, 100)}%` }}
                                />
                            </div>
                            <p className="text-xs text-gray-500 text-right">
                                {paymentSummary.payment_progress.toFixed(1)}% complete
                            </p>
                        </div>
                    )}

                    {/* Lease Information */}
                    <div className="border-t pt-4">
                        <h3 className="font-semibold text-gray-900 mb-3">Lease Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <User className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Tenant:</span>
                                    <span className="text-sm font-medium ml-2">
                                        {currentLease.first_name || 'N/A'} {currentLease.last_name || ''}
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <Home className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Property:</span>
                                    <span className="text-sm ml-2">{currentLease.asset_name || 'N/A'}</span>
                                </div>
                                <div className="flex items-center">
                                    <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Unit:</span>
                                    <span className="text-sm ml-2">{currentLease.unit_number || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Lease Period:</span>
                                    <span className="text-sm ml-2">
                                        {currentLease.start_date ? new Date(currentLease.start_date).toLocaleDateString() : 'N/A'} - 
                                        {currentLease.end_date ? new Date(currentLease.end_date).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Security Deposit:</span>
                                    <span className="text-sm ml-2">{formatCurrency(currentLease.deposit_amount)}</span>
                                </div>
                                <div className="flex items-center">
                                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Payment Due Day:</span>
                                    <span className="text-sm ml-2">Day {currentLease.payment_due_day || 1} of each month</span>
                                </div>
                                <div className="flex items-center">
                                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                    <span className="text-sm text-gray-600">Billing Cycle:</span>
                                    <span className="text-sm ml-2 font-medium capitalize">{currentLease.billing_cycle || 'Monthly'}</span>
                                    <button
                                        onClick={() => {
                                            const newCycle = prompt('Enter billing cycle (monthly, weekly, daily, termly, milestone):', currentLease.billing_cycle || 'monthly');
                                            if (newCycle && ['monthly', 'weekly', 'daily', 'termly', 'milestone'].includes(newCycle)) {
                                                api.put(`/leases/${currentLease.id}`, { billing_cycle: newCycle })
                                                    .then(() => {
                                                        alert('Billing cycle updated!');
                                                        fetchLeaseDetails();
                                                    })
                                                    .catch(err => alert('Failed to update billing cycle'));
                                            } else if (newCycle) {
                                                alert('Invalid. Use: monthly, weekly, daily, termly, or milestone.');
                                            }
                                        }}
                                        className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded hover:bg-indigo-200 transition-colors"
                                        title="Change billing cycle"
                                    >
                                        Change
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment History */}
                    <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-semibold text-gray-900">Payment History</h3>
                            <button
                                onClick={() => setShowPaymentModal(true)}
                                className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 print:hidden"
                            >
                                Record Payment
                            </button>
                        </div>
                        
                        {loading ? (
                            <div className="text-center py-4">Loading payments...</div>
                        ) : payments.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">No payments recorded yet.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {payments.map((payment) => (
                                            <tr key={payment.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 text-sm">
                                                    {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="px-4 py-2 text-sm font-medium">
                                                    {formatCurrency(payment.amount)}
                                                </td>
                                                <td className="px-4 py-2 text-sm capitalize">
                                                    {payment.payment_type || 'rent'}
                                                </td>
                                                <td className="px-4 py-2 text-sm capitalize">
                                                    {payment.payment_method?.replace('_', ' ') || '-'}
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                    {payment.payment_period_start ? (
                                                        `${new Date(payment.payment_period_start).toLocaleDateString()} - ${new Date(payment.payment_period_end).toLocaleDateString()}`
                                                    ) : '-'}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadge(payment.status)}`}>
                                                        {getStatusIcon(payment.status)}
                                                        <span className="ml-1">{payment.status}</span>
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2">
                                                    <div className="flex space-x-1">
                                                        {payment.status === 'pending' && (
                                                            <button
                                                                onClick={() => markAsCompleted(payment.id)}
                                                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                                                title="Mark as Paid"
                                                            >
                                                                Mark Paid
                                                            </button>
                                                        )}
                                                        {payment.mpesa_receipt_number && (
                                                            <button
                                                                onClick={() => downloadReceipt(payment.id)}
                                                                className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                                                                title="Download Receipt"
                                                            >
                                                                <Download className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Terms and Conditions */}
                    {currentLease.terms_and_conditions && (
                        <div className="border-t pt-4">
                            <h3 className="font-semibold text-gray-900 mb-2">Terms & Conditions</h3>
                            <div className="bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                    {currentLease.terms_and_conditions}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Deposit Refund Modal */}
            <DepositRefundModal
                isOpen={showDepositRefundModal}
                onClose={() => {
                    setShowDepositRefundModal(false);
                    fetchLeaseDetails();
                    if (onPaymentComplete) onPaymentComplete();
                }}
                lease={currentLease}
                onRefundComplete={() => {
                    setShowDepositRefundModal(false);
                    fetchLeaseDetails();
                    fetchPayments();
                    if (onPaymentComplete) onPaymentComplete();
                }}
            />

            {/* Payment Modal for recording payments */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false);
                    fetchPayments();
                    if (onPaymentComplete) onPaymentComplete();
                }}
                lease={currentLease}
                onPaymentComplete={() => {
                    fetchPayments();
                    if (onPaymentComplete) onPaymentComplete();
                }}
            />

            {/* Transfer Modal */}
            <TransferModal
                isOpen={showTransferModal}
                onClose={() => {
                    setShowTransferModal(false);
                    if (onPaymentComplete) onPaymentComplete();
                }}
                lease={currentLease}
                onTransferComplete={() => {
                    setShowTransferModal(false);
                    onClose();
                    if (onPaymentComplete) onPaymentComplete();
                }}
            />
        </>
    );
};

export default LeaseDetailsModal;